import { addAction, addError } from "@wayflyer/blackbox-fe";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { Outlet, createBrowserRouter, useRouteError, type RouteObject } from "react-router";
import { ErrorComponent, getCustomerErrorPresetMap, getErrorVariant } from "shared-ui";

import type { CustomerRouteAuthentication } from "./authn/authenticated-customer-route";
import { Fallback } from "./fallback";
import { documentsRedirect, loginRedirect, settingsRedirect, signupRedirect } from "./legacy-route-redirects";
import { useIntercomLoaded } from "./libs/app/intercom";
import { ErrorPage, RootComponent } from "./libs/app/renderRootComponent";
import { customerRoutePattern } from "./paths";

const chunkLoadingErrorMsgs = [
  "Failed to fetch dynamically imported module", // Chrome
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari
];

const fiveMinutes = 5 * 60 * 1000;

const loopProtectedReloadKey = "wf-last-reload-time";

const loopProtectedReload = (error: TypeError) => {
  const lastReloadTime = sessionStorage.getItem(loopProtectedReloadKey);

  if (lastReloadTime && Date.now().valueOf() - Number(lastReloadTime) < fiveMinutes) {
    addError("Attempted to reload due to chunk loading error within five minutes", { cause: error });
    return;
  }

  sessionStorage.setItem(loopProtectedReloadKey, Date.now().valueOf().toString());
  window.location.reload();
};

const ErrorElement = () => {
  const error = useRouteError();
  useIntercomLoaded();
  return <DefaultErrorElement error={error} />;
};

const DefaultErrorElement = (props: { error: any }) => {
  const { error } = props;

  const variant = getErrorVariant(error);
  const intl = useIntl();
  const { title, message, footerText, primaryAction } = getCustomerErrorPresetMap({ intl })[variant];

  useEffect(() => {
    // An older document can reference chunks removed by a later release.
    if (error instanceof TypeError && chunkLoadingErrorMsgs.some((msg) => error.message.includes(msg))) {
      addAction("Failed to fetch dynamically imported module, reloading", { cause: error });
      loopProtectedReload(error);
    } else {
      addError(error);
    }
  }, [error]);

  return (
    <ErrorComponent
      {...{
        title,
        message,
        footerText,
        primaryAction,
        secondaryAction: {
          content: intl.formatMessage({ id: "error.goBack", defaultMessage: "Go back" }),
          onAction: () => window.history.back(),
        },
      }}
    />
  );
};

type CustomerRouteHandle = {
  authenticated?: CustomerRouteAuthentication;
  title?: string;
};

type CustomerRouteObject = RouteObject & {
  handle?: CustomerRouteHandle;
};

const isAuthenticatedRoute = (route: CustomerRouteObject) => route.handle?.authenticated !== false;

const authenticatedByDefault = (customerRoutes: CustomerRouteObject[]): RouteObject[] => {
  const authenticatedRoutes = customerRoutes.filter(isAuthenticatedRoute);

  return [
    ...customerRoutes.filter((route) => !isAuthenticatedRoute(route)),
    {
      id: "authenticated-customer-routes",
      lazy: () => import("./authn/authenticated-customer-route"),
      children: authenticatedRoutes,
    },
  ];
};

// Exported so tests can mount the real route tree under their own shell.
export const routes: RouteObject[] = [
  {
    path: "/",
    handle: {
      title: "Wayflyer",
    },
    element: (
      <RootComponent>
        <Outlet />
      </RootComponent>
    ),
    errorElement: (
      <ErrorPage>
        <ErrorElement />
      </ErrorPage>
    ),
    HydrateFallback: Fallback,
    children: authenticatedByDefault([
      {
        path: "/",
        lazy: () => import("./entry-points/home"),
        handle: {
          title: "Home",
        },
      },
      {
        path: "/login",
        loader: loginRedirect,
        handle: { authenticated: false },
      },
      {
        path: "/signup",
        loader: signupRedirect,
        handle: { authenticated: false },
      },
      {
        path: "/tracked-login/:token",
        lazy: () => import("./tracked-login"),
        handle: { authenticated: false },
      },
      {
        // TODO: things under /auth will be handled on the server side
        // and as such, should not render UI, just redirect
        path: "/auth",
        handle: { authenticated: false },
        children: [
          {
            path: "bigcommerce/load",
            lazy: () => import("./integrations/bigcommerce-iframe/bigcommerce-load"),
            handle: {
              title: "Wayflyer for BigCommerce",
            },
          },
          {
            path: "bigcommerce/auth",
            lazy: () => import("./integrations/bigcommerce-iframe/bigcommerce-auth"),
            handle: {
              title: "Wayflyer for BigCommerce",
            },
          },
        ],
      },
      {
        path: "/authn-wf/callback",
        lazy: () => import("./authn/callback"),
        handle: { authenticated: false },
      },
      {
        path: "/authn-wf/callback-transaction",
        lazy: () => import("./authn/callback-transaction"),
        handle: { authenticated: false },
      },
      // ===> Local development only: uncomment/see for example of authn utils.
      // {
      //   path: "/authn-wf/example",
      //   lazy: () => import("./authn/example"),
      // },
      // {
      //   path: "/authn-wf/example-hook",
      //   lazy: () => import("./authn/example-hook"),
      // },
      // <======================================================================
      {
        path: "/beta-signup-wfbank",
        lazy: async () => {
          const { Component } = await import("./product-choice");
          return {
            Component: () => <Component product="banking" />,
          };
        },
        handle: {
          title: "Banking",
          authenticated: { authPath: "signup" },
        },
      },
      {
        path: "/wholesale",
        lazy: async () => {
          const { Component } = await import("./product-choice");
          return {
            Component: () => <Component product="funding" />,
          };
        },
        handle: {
          title: "Wholesale",
          authenticated: { authPath: "signup" },
        },
      },
      {
        path: "/intelligence",
        lazy: async () => {
          const { Component } = await import("./product-choice");
          return {
            Component: () => <Component product="intelligence" />,
          };
        },
        handle: {
          title: "Intelligence",
          authenticated: { authPath: "signup" },
        },
      },
      {
        path: "/company-creation/invite/already-accepted",
        lazy: () => import("./entry-points/company_creation/invite-already-accepted"),
        handle: {
          title: "Invite already used",
          authenticated: false,
        },
      },
      {
        path: customerRoutePattern("company-creation:spa"),
        lazy: () => import("./entry-points/company_creation/app"),
        handle: {
          title: "Company Details",
        },
      },
      {
        path: "/documents",
        handle: {
          title: "Documents",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/routines",
        handle: {
          title: "Routines",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/customer-referrals",
        handle: {
          title: "Customer Referrals",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/financial-os-banking",
        handle: {
          title: "Financial OS Banking",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/financing/ach-stripe",
        handle: {
          title: "ACH Stripe",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/financing/bank-statements",
        handle: {
          title: "Upload Bank Statements",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/financing/bank-statements/connect",
        handle: {
          title: "Connect Bank Statements",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/financing/bank-statements/connect/basiq",
        handle: {
          title: "Connect Bank Statements via Basiq",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/financing/bank-statements/upload",
        handle: {
          title: "Upload Bank Statements",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/financing/funds-request",
        handle: {
          title: "Funds Request",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/funding/application/business-model",
        handle: {
          title: "Apply for Funding",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/funding/application/connections/banking",
        handle: {
          title: "Apply for Funding",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/funding/how-to-apply",
        lazy: () => import("./entry-points/funding_application/how-to-apply-app"),
        handle: {
          title: "Apply for Funding",
          authenticated: false,
        },
      },
      {
        path: "/funding/wayflyer-hosted-capital",
        lazy: () => import("./entry-points/hosted-capital/app"),
        handle: {
          title: "Wayflyer Hosted Capital",
          authenticated: false,
        },
      },
      {
        path: "/settings-connections",
        handle: {
          title: "Connect your store",
          authenticated: { customerPath: "/settings/company/connections" },
        },
      },
      {
        path: "/settings/connections",
        handle: {
          title: "Connections",
          authenticated: { customerPath: "/settings/company/connections" },
        },
      },
      {
        path: "/settings/connections/connect-accounting",
        handle: {
          title: "Connect your store",
          authenticated: { customerPath: true },
        },
      },
      {
        // Slug-less landing target for the Plaid OAuth return on the new company
        // connections page. Plaid redirect URIs are registered without the customer
        // slug, so this recovers the customer and forwards (preserving oauth_state_id)
        // to /:customerDomain/settings/company/connections.
        path: "/settings/company/connections",
        handle: {
          title: "Connections",
          authenticated: { customerPath: true },
        },
      },
      {
        path: "/upload-documents",
        handle: {
          title: "Upload Documents",
          authenticated: { customerPath: "/funding/upload-documents" },
        },
      },
      {
        path: customerRoutePattern("kyc-public:verify-identity").replace(/\/$/, ""),
        lazy: () => import("./entry-points/kyc/verify-identity"),
        handle: {
          title: "Verify your identity",
          authenticated: false,
        },
      },
      {
        path: customerRoutePattern("kyc-public:verify-identity-v2").replace(/\/$/, ""),
        lazy: () => import("./entry-points/kyc-v3/verify-identity-unauthenticated"),
        handle: {
          title: "Verify your identity",
          authenticated: false,
        },
      },
      {
        path: customerRoutePattern("kyc:verify-identity-authenticated"),
        lazy: () => import("./entry-points/kyc-v3/verify-identity-authenticated"),
        handle: {
          title: "Verify your identity",
        },
      },
      {
        path: `${customerRoutePattern("home")}?`,
        lazy: () => import("./entry-points/home/customer-home"),
        handle: {
          title: "Home",
        },
      },
      {
        path: customerRoutePattern("documents"),
        loader: documentsRedirect,
      },
      {
        path: customerRoutePattern("settings:settings"),
        loader: settingsRedirect,
      },
      {
        path: customerRoutePattern("banking-screen"),
        lazy: () => import("./entry-points/banking-app/banking-application"),
        handle: {
          title: "Banking",
        },
      },
      {
        path: customerRoutePattern("banking-mfa"),
        lazy: () => import("./banking/banking-mfa-setup-screen/banking-mfa-setup"),
        handle: {
          title: "Banking",
        },
      },
      {
        path: customerRoutePattern("banking-screen-catch-all"),
        lazy: () => import("./entry-points/banking-app/app"),
        handle: {
          title: "Banking",
        },
      },
      {
        path: customerRoutePattern("configure-banking"),
        lazy: () => import("./entry-points/banking-app/bank-beta-signup"),
        handle: {
          title: "Banking",
        },
      },
      {
        path: customerRoutePattern("connections-redirect"),
        lazy: () => import("./settings/connections-redirect"),
        handle: {
          title: "Connect your store",
        },
      },
      {
        path: customerRoutePattern("customer-referrals"),
        lazy: () => import("./customer-referrals/customer-referrals-screen/customer-referrals-screen"),
        handle: {
          title: "Customer Referrals",
        },
      },
      {
        path: customerRoutePattern("financing-contract-order-form"),
        lazy: () => import("./financing/contracts/screens/mca-order-form-screen/mca-order-form-screen"),
        handle: {
          title: "Financing Contract Order Form",
        },
      },
      {
        path: customerRoutePattern("financing-contract-disclosures"),
        lazy: () => import("./financing/contracts/screens/disclosure-screen/disclosure-screen"),
        handle: {
          title: "Financing Contract Disclosures",
        },
      },
      {
        path: customerRoutePattern("financing-contract-bank-details"),
        lazy: () => import("./financing/contracts/screens/bank-account-info-screen/bank-account-info-screen"),
        handle: {
          title: "Financing Contract Bank Details",
        },
      },
      {
        path: `${customerRoutePattern("financing-reconnect-sub")}?`,
        lazy: () => import("./financing/reconnect"),
        handle: {
          title: "Reconnect platforms",
        },
      },
      {
        path: `${customerRoutePattern("financing-dashboard")}/*`,
        lazy: () => import("./financing/financing"),
        handle: {
          title: "Financing",
        },
      },
      {
        path: customerRoutePattern("financing-ach-stripe"),
        lazy: () => import("./financing/bank-connect/ach-stripe/ach-stripe"),
      },
      {
        path: customerRoutePattern("financing-bank-statements-start"),
        lazy: () => import("./financing/upload-bank-statements"),
        handle: {
          title: "Upload your bank statements",
        },
      },
      {
        path: customerRoutePattern("financing-bank-statements-connect"),
        lazy: () => import("./financing/upload-bank-statements"),
        handle: {
          title: "Upload your bank statements",
        },
      },
      {
        path: customerRoutePattern("financing-bank-statements-upload"),
        lazy: () => import("./financing/bank-statements-upload/bank-statements-upload-screen"),
        handle: {
          title: "Upload your bank statements",
        },
      },
      {
        path: customerRoutePattern("financing-direct-debit"),
        lazy: () => import("./financing/direct-debit/direct-debit"),
        handle: {
          title: "Upload your bank statements",
        },
      },
      {
        path: `${customerRoutePattern("financing-funds-request")}/*`,
        lazy: () => import("./financing/funds-request"),
        handle: {
          title: "Financing",
        },
      },
      {
        path: customerRoutePattern("statements"),
        lazy: () => import("./financing/statements"),
        handle: {
          title: "Generate transaction statement",
        },
      },
      {
        path: customerRoutePattern("financing-accounting-write-back"),
        lazy: () => import("./financing/write-back/write-back"),
        handle: {
          title: "Xero Sync Integration",
        },
      },
      {
        path: `${customerRoutePattern("funding-application:spa")}?`,
        lazy: () => import("./entry-points/funding_application/app"),
        handle: {
          title: "Apply for Funding",
        },
      },
      {
        // This is temporary while under development. Will be moved into customer-home once ready for release.
        path: customerRoutePattern("wayflyer-home"),
        lazy: () => import("./home"),
        handle: {
          title: "Home",
        },
      },
      {
        path: customerRoutePattern("invites-handler"),
        lazy: () => import("./entry-points/company_creation/invite-request"),
        handle: {
          title: "Invite Requests",
        },
      },
      {
        path: customerRoutePattern("insights:overview"),
        lazy: () => import("./entry-points/insights/overview-dashboard"),
        handle: {
          title: "Performance - Overview",
        },
      },
      {
        path: customerRoutePattern("bills"),
        lazy: () => import("./entry-points/bills/page"),
        handle: {
          title: "Bills",
        },
      },
      {
        path: customerRoutePattern("bills:bill"),
        lazy: () => import("./entry-points/bills/bill"),
        handle: {
          title: "Bills",
        },
      },
      {
        path: customerRoutePattern("insights:channels"),
        lazy: () => import("./entry-points/insights/channels"),
        handle: {
          title: "Performance - Channels",
        },
      },
      {
        path: customerRoutePattern("insights:google"),
        lazy: () => import("./entry-points/insights/googleads"),
        handle: {
          title: "Performance - Google Ads",
        },
      },
      {
        path: customerRoutePattern("insights:internal-overview"),
        lazy: () => import("./entry-points/insights/internal-overview-dashboard"),
        handle: {
          title: "Performance - Overview",
        },
      },
      {
        path: customerRoutePattern("insights:consumers"),
        lazy: () => import("./entry-points/insights/consumers"),
        handle: {
          title: "Lifetime Value",
        },
      },
      {
        path: customerRoutePattern("insights:internal-ltv-cac"),
        lazy: () => import("./entry-points/insights/internal-ltv-cac"),
        handle: {
          title: "Performance - LTV-CAC",
        },
      },
      {
        path: customerRoutePattern("insights:meta"),
        lazy: () => import("./entry-points/insights/meta-ads"),
        handle: {
          title: "Performance - Meta Ads",
        },
      },
      {
        path: customerRoutePattern("insights:facebook"),
        lazy: () => import("./entry-points/insights/facebook"),
        handle: {
          title: "Performance - Meta Ads",
        },
      },
      {
        path: customerRoutePattern("insights:paid-channel-summary"),
        lazy: () => import("./entry-points/insights/paid-channel-summary"),
        handle: {
          title: "Paid Channels",
        },
      },
      {
        path: customerRoutePattern("insights:products"),
        lazy: () => import("./entry-points/insights/products"),
        handle: {
          title: "Performance - Products",
        },
      },
      {
        path: customerRoutePattern("insights:internal-revenue"),
        lazy: () => import("./entry-points/insights/internal-revenue"),
        handle: {
          title: "Performance - Revenue",
        },
      },
      {
        path: customerRoutePattern("insights:internal-tiktok-spend"),
        lazy: () => import("./entry-points/insights/internal-tiktok-ad-spend"),
        handle: {
          title: "Performance - TikTok Ad Spend",
        },
      },
      {
        path: customerRoutePattern("intelligence-chat"),
        lazy: () => import("./entry-points/intelligence/chat"),
        handle: {
          title: "Intelligence",
        },
      },
      {
        path: customerRoutePattern("intelligence-routines"),
        lazy: () => import("./entry-points/intelligence/routines"),
        handle: {
          title: "Routines",
        },
      },
      {
        path: customerRoutePattern("checkout"),
        lazy: () => import("./entry-points/checkout-home/home"),
        handle: {
          title: "Checkout",
        },
      },
      {
        path: customerRoutePattern("company-remediation-subroute"),
        lazy: () => import("./entry-points/company_creation/remediation"),
        handle: {
          title: "Company Remediation",
        },
      },
      {
        path: "/:customerDomain/settings/team-member/:memberId",
        lazy: () => import("./entry-points/profile-spaces/edit-team-member"),
        handle: {
          title: "Edit Team Member",
        },
      },
      {
        path: customerRoutePattern("settings-connections-reconnect"),
        lazy: () => import("./reconnect/app"),
        handle: {
          title: "Reconnect platforms",
        },
      },
      // The legacy connections page and its connect sub-pages are gone; these redirect to the
      // company connections page. Listed explicitly rather than as a `/settings/connections/*`
      // splat so the reconnect route above can never be swallowed by the redirect.
      ...["", "/connect", "/connect-marketing", "/connect-accounting"].map((suffix) => ({
        path: `/:customerDomain/settings/connections${suffix}`,
        lazy: () => import("./settings/connections-redirect"),
        handle: {
          title: "Connections",
        },
      })),
      {
        // Profile-pages IA (PFF-472). The whole section is ONE lazy chunk: both
        // sections import the same `profile-spaces/app` module (each picking its
        // own sub-router export), and each sub-router uses a descendant <Routes>
        // for its screens. The account section is user-scoped, so it's mounted
        // domain-less; the company section stays under `/:customerDomain/` since
        // it needs a company.
        path: customerRoutePattern("user-profile:settings-account-sub"),
        lazy: async () => ({ Component: (await import("./entry-points/profile-spaces/app")).AccountApp }),
        handle: {
          title: "Account",
        },
      },
      {
        // Splat-mounted per section (not /settings/*) so the sibling
        // /settings/team-member and /settings/connections routes keep matching.
        path: customerRoutePattern("settings-company-sub"),
        lazy: async () => ({ Component: (await import("./entry-points/profile-spaces/app")).CompanyApp }),
        handle: {
          title: "Company",
        },
      },
      {
        path: customerRoutePattern("kyc:verify-business"),
        lazy: () => import("./entry-points/kyc/verify-business"),
        handle: {
          title: "Verify your business",
        },
      },
      {
        path: customerRoutePattern("kyc:verify-company"),
        lazy: () => import("./entry-points/kyc-v3/verify-company"),
        handle: {
          title: "Verify your company",
        },
      },
      {
        path: customerRoutePattern("kyc:verify-business-airs-review").replace(":path", ":kycAirId"),
        lazy: () => import("./entry-points/kyc/verify-business-air"),
        handle: {
          title: "Verify your identity",
        },
      },
      {
        path: "/400",
        lazy: () => import("./errors/error400"),
        handle: {
          title: "Bad request",
          authenticated: false,
        },
      },
      {
        path: "/403",
        lazy: () => import("./errors/error403"),
        handle: {
          title: "Access denied",
          authenticated: false,
        },
      },
      {
        path: "/404",
        lazy: () => import("./errors/error404"),
        handle: {
          title: "Page not found",
          authenticated: false,
        },
      },
      {
        path: "/500",
        lazy: () => import("./errors/error500"),
        handle: {
          title: "Error",
          authenticated: false,
        },
      },
      {
        path: "/authn-wf/account-deactivated",
        lazy: () => import("./errors/errorAuthnAccountDeactivated"),
        handle: {
          title: "Account deactivated",
          authenticated: false,
        },
      },
      {
        path: "/authn-wf/email-constraint",
        lazy: () => import("./errors/errorAuthnEmailConstraint"),
        handle: {
          title: "Login method error",
          authenticated: false,
        },
      },
      {
        path: "/authn-wf/error",
        lazy: () => import("./errors/errorAuthnGeneric"),
        handle: {
          title: "Authentication error",
          authenticated: false,
        },
      },
      {
        path: "*",
        lazy: async () => {
          const { Component } = await import("./errors/error404");
          return {
            Component: () => <Component withLogging />,
          };
        },
        handle: {
          title: "Page not found",
          authenticated: false,
        },
      },
    ]),
  },
];

export const router = createBrowserRouter(routes);
